import classNames from 'classnames'
import {
    DetailedHTMLProps,
    InputHTMLAttributes,
    ReactNode,
    Ref,
    TextareaHTMLAttributes,
    useId,
} from 'react'

import { FlexCol } from '../../../../common/frontend/components/flex-col/flex-col.js'

export type InputSize = 'regular' | 'small'

export type TextFieldAs = 'input' | 'textarea'

export interface TextFieldNoteProps {
    text: ReactNode
    isError?: boolean
}

export interface TextFieldProps {
    className?: string
    wrapperClass?: string
    labelClass?: string
    label?: string
    type?: 'text' | 'password'
    as?: TextFieldAs
    value?: string
    onChange?: (value: string) => void
    placeholder?: string
    isRequired?: boolean
    autoFocus?: boolean // TODO
    isDisabled?: boolean
    size?: InputSize
    prefix?: ReactNode
    suffix?: ReactNode
    inputClass?: string
    inputRef?: Ref<HTMLInputElement>
    inputProps?: DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>
    textAreaRef?: Ref<HTMLTextAreaElement>
    textAreaProps?: DetailedHTMLProps<
        TextareaHTMLAttributes<HTMLTextAreaElement>,
        HTMLTextAreaElement
    >
    note?: TextFieldNoteProps
    onEnter?: () => void
}

export const TextField = (props: TextFieldProps) => {
    // Associate the label with the control by id rather than wrapping the whole
    // field in a <label>. A wrapping label makes the entire container (gaps,
    // padding, the row beside the text) a click target that focuses the input —
    // for a Select that reopens the dropdown, making the popover hard to close.
    const generatedId = useId()
    const controlId = props.inputProps?.id ?? props.textAreaProps?.id ?? generatedId

    return (
        <FlexCol className={classNames('text-field', props.className)} gap={0.5}>
            <div className={classNames('text-field__container', props.labelClass)}>
                {props.label && (
                    <label className="text-field__label" htmlFor={controlId}>
                        {props.label}
                        {props.isRequired && (
                            <span className="text-field__required-asterisk"> *</span>
                        )}
                    </label>
                )}
                <span
                    className={classNames(
                        'text-field__input-wrapper',
                        props.size && `text-field__input-wrapper--${props.size}`,
                        props.isDisabled && 'text-field__input-wrapper--disabled',
                        props.wrapperClass,
                    )}
                >
                    {props.prefix}
                    {props.as === 'textarea' ? (
                        <textarea
                            {...props.textAreaProps}
                            id={controlId}
                            ref={props.textAreaRef}
                            className={classNames(
                                'text-field__input',
                                'text-field__input--textarea',
                                props.isDisabled && 'text-field__input--disabled',
                                props.inputClass,
                                props.textAreaProps?.className,
                            )}
                            value={
                                (props.textAreaProps?.value as string | undefined) ??
                                props.value ??
                                ''
                            }
                            onChange={
                                props.textAreaProps?.onChange ??
                                ((event) => {
                                    props.onChange?.(event.currentTarget.value)
                                })
                            }
                            disabled={props.textAreaProps?.disabled ?? props.isDisabled}
                            placeholder={props.textAreaProps?.placeholder ?? props.placeholder}
                        />
                    ) : (
                        <input
                            {...props.inputProps}
                            id={controlId}
                            ref={props.inputRef}
                            className={classNames(
                                'text-field__input',
                                props.isDisabled && 'text-field__input--disabled',
                                props.inputClass,
                                props.inputProps?.className,
                            )}
                            type={props.inputProps?.type ?? props.type ?? 'text'}
                            value={
                                (props.inputProps?.value as string | undefined) ?? props.value ?? ''
                            }
                            onChange={
                                props.inputProps?.onChange ??
                                ((event) => {
                                    props.onChange?.(event.currentTarget.value)
                                })
                            }
                            onKeyUp={(event) => {
                                props.inputProps?.onKeyUp?.(event)

                                if (props.onEnter && event.key === 'Enter') {
                                    props.onEnter?.()
                                }
                            }}
                            disabled={props.inputProps?.disabled ?? props.isDisabled}
                            placeholder={props.inputProps?.placeholder ?? props.placeholder}
                        />
                    )}
                    {props.suffix}
                </span>
                {props.note && (
                    <div
                        className={classNames(
                            'text-field__note',
                            props.note.isError && 'text-field__note--error',
                        )}
                    >
                        {props.note.text}
                    </div>
                )}
            </div>
        </FlexCol>
    )
}
